import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ColorScheme, ThemeState } from '@/types';

/**
 * Theme store with localStorage persistence.
 *
 * Manages the color scheme preference (solarized-light/dark).
 * Automatically syncs with the DOM via data-theme attribute.
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      colorScheme: 'solarized-light',

      setColorScheme: (scheme: ColorScheme) => {
        document.documentElement.setAttribute('data-theme', scheme);
        set({ colorScheme: scheme });
      },

      toggleColorScheme: () => {
        const current = get().colorScheme;
        const next = current === 'solarized-light' ? 'solarized-dark' : 'solarized-light';
        document.documentElement.setAttribute('data-theme', next);
        set({ colorScheme: next });
      },
    }),
    {
      name: 'fsc-theme',
      onRehydrateStorage: () => (state) => {
        // Apply theme to DOM after rehydration from localStorage
        if (state?.colorScheme) {
          document.documentElement.setAttribute('data-theme', state.colorScheme);
        }
      },
    }
  )
);

/**
 * Initialize theme on app load.
 * Call this once in the app entry point.
 */
export function initializeTheme(): void {
  const state = useThemeStore.getState();
  document.documentElement.setAttribute('data-theme', state.colorScheme);
}
